// app/src/Voice.jsx — progressive-enhancement voice-to-text mic button.
//
// Uses the browser Web Speech API (webkitSpeechRecognition). It renders NOTHING
// where the API is unavailable (e.g. Firefox), so it's a pure enhancement. Speech
// is transcribed in the member's own language (preferredLang → BCP-47 locale) and
// the final text is handed to onText, which the composer drops into its draft for
// the user to review/edit before posting — it's never auto-sent.
//
// Privacy: on Chrome the browser streams the audio to Google's recognizer (this is
// the browser's own behavior, not a call we make). Covered in the privacy policy.

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

// Map a member preferredLang (2-letter) to a Web Speech recognition locale.
const RECOGNITION_LOCALE = {
  en: 'en-US', es: 'es-ES', th: 'th-TH', zh: 'zh-CN', vi: 'vi-VN',
  fr: 'fr-FR', ar: 'ar-SA', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', el: 'el-GR',
};

function micErrorMessage(code) {
  if (code === 'not-allowed' || code === 'service-not-allowed') return t('voice.denied', 'Microphone blocked — allow mic access in your browser settings, then try again.');
  if (code === 'no-speech') return t('voice.noSpeech', "Didn't catch that — try again.");
  if (code === 'audio-capture') return t('voice.noMic', 'No microphone found.');
  if (code === 'network') return t('voice.network', 'Network error — check your connection.');
  return t('voice.error', "Couldn't start voice input.");
}

function MicButton({ lang, onText, disabled, size = 34 }) {
  const [listening, setListening] = React.useState(false);
  const [err, setErr] = React.useState('');
  const recRef = React.useRef(null);
  const errTimer = React.useRef(null);

  // Stop any in-flight recognition + clear timers if the composer unmounts.
  React.useEffect(() => () => {
    try { recRef.current && recRef.current.stop(); } catch { /* ignore */ }
    clearTimeout(errTimer.current);
  }, []);

  // Progressive enhancement: no API → no button at all.
  if (!SpeechRecognition) return null;

  const flashErr = (msg) => {
    setErr(msg);
    clearTimeout(errTimer.current);
    errTimer.current = setTimeout(() => setErr(''), 4500);
  };

  const toggle = () => {
    if (listening) { try { recRef.current && recRef.current.stop(); } catch { /* ignore */ } return; }
    setErr('');
    let rec;
    try { rec = new SpeechRecognition(); } catch { flashErr(micErrorMessage()); return; }
    rec.lang = RECOGNITION_LOCALE[lang] || lang || 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const text = Array.from(e.results)
        .map((r) => (r[0] && r[0].transcript) || '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (text) onText(text);
    };
    rec.onerror = (e) => { setListening(false); if (e && e.error !== 'aborted') flashErr(micErrorMessage(e && e.error)); };
    rec.onend = () => { setListening(false); recRef.current = null; };
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch { setListening(false); flashErr(micErrorMessage()); }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
      <button onClick={toggle} disabled={disabled} aria-pressed={listening}
        title={listening ? t('voice.listening', 'Listening… tap to stop') : t('voice.dictate', 'Dictate')}
        style={{
          border: '1px solid var(--line)', borderRadius: 999, width: size, height: size, flex: 'none',
          fontSize: Math.round(size * 0.47), padding: 0,
          background: listening ? 'var(--primary)' : 'var(--surface-2)',
          color: listening ? 'var(--primary-ink)' : (disabled ? 'var(--sub)' : 'var(--primary)'),
          boxShadow: listening ? '0 0 0 3px var(--primary-soft)' : 'none',
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
        }}>🎤</button>
      {err && (
        <span role="alert" style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8,
          width: 180, whiteSpace: 'normal', textAlign: 'center', background: 'var(--ink)', color: 'var(--bg)',
          fontSize: 11, lineHeight: 1.35, padding: '6px 9px', borderRadius: 8, zIndex: 30,
          boxShadow: '0 4px 14px rgba(0,0,0,.18)',
        }}>{err}</span>
      )}
    </span>
  );
}

Object.assign(window, { MicButton });
